(function () {
  'use strict';

  function productId(value) {
    var match = String(value || '').trim().match(/^(?:gid:\/\/shopify\/Product\/)?([1-9]\d*)$/);
    if (!match) throw new Error('Enter a numeric Shopify product ID or a Dropbox image link.');
    return match[1];
  }

  function directDropboxUrl(value) {
    var url;
    try { url = new URL(value); } catch (_) { throw new Error('Enter a valid Dropbox image link.'); }
    var hosts = ['dropbox.com', 'www.dropbox.com', 'dl.dropbox.com', 'dl.dropboxusercontent.com'];
    if (url.protocol !== 'https:' || hosts.indexOf(url.hostname) === -1 || url.username || url.password || url.port) {
      throw new Error('Use an HTTPS Dropbox shared image link.');
    }
    url.hash = '';
    url.searchParams.delete('dl');
    url.searchParams.delete('raw');
    if (url.pathname.indexOf('/scl/fi/') === 0) {
      url.searchParams.set('raw', '1');
    } else if (url.pathname.indexOf('/s/') === 0) {
      url.hostname = 'dl.dropboxusercontent.com';
      url.searchParams.set('dl', '1');
    } else {
      throw new Error('Use a Dropbox shared link to an image file, rather than a folder.');
    }
    return url.href;
  }

  function sourceFromQuery(search) {
    var params = new URLSearchParams(search);
    if (params.has('preview')) return decodePreview(params.get('preview'));
    var dropbox = params.get('dropbox') || params.get('dropbox_url');
    var id = params.get('product_id') || params.get('productId');
    // A Dropbox proof can override the original product artwork.
    if (dropbox) return { type: 'dropbox', value: dropbox, url: directDropboxUrl(dropbox) };
    if (id) return { type: 'product', value: productId(id) };
    return null;
  }

  function sourceFromInput(value) {
    value = String(value || '').trim();
    return /^https:\/\//i.test(value)
      ? { type: 'dropbox', value: value, url: directDropboxUrl(value) }
      : { type: 'product', value: productId(value) };
  }

  function encodePreview(source) {
    // Obscures the share URL in the address bar; this is not encryption or access control.
    var bytes = new TextEncoder().encode(JSON.stringify({ v: 1, type: source.type, value: source.value }));
    var binary = '';
    bytes.forEach(function (byte) { binary += String.fromCharCode(byte); });
    var token = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    if (token.length > 8192) throw new Error('This Dropbox link is too long. Please use a shorter shared link.');
    return token;
  }

  function decodePreview(token) {
    try {
      if (!token || token.length > 8192 || !/^[A-Za-z0-9_-]+$/.test(token)) throw new Error();
      var binary = atob(token.replace(/-/g, '+').replace(/_/g, '/'));
      var bytes = Uint8Array.from(binary, function (character) { return character.charCodeAt(0); });
      var payload = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
      if (payload.v !== 1 || !['dropbox', 'product'].includes(payload.type) || typeof payload.value !== 'string') throw new Error();
      var source = sourceFromInput(payload.value);
      if (source.type !== payload.type) throw new Error();
      return source;
    } catch (_) { throw new Error('This preview link is invalid. Please ask the sender for a new link.'); }
  }

  function giftMessage(value) {
    var text = String(value).replace(/\r\n?/g, '\n');
    if (text.length > 2000) throw new Error('Please keep the gift message to 2,000 characters or fewer.');
    return text;
  }

  function giftField(key, value) {
    var limits = { eyebrow: 120, heading: 200, signoff: 200 };
    var text = String(value).replace(/\r\n?/g, '\n');
    if (!limits[key] || text.length > limits[key]) throw new Error('Please shorten the ' + key + ' to ' + limits[key] + ' characters or fewer.');
    return text;
  }

  function createPreviewLink(pageUrl, source, text, copy) {
    source = sourceFromInput(source.value);
    var url = new URL(pageUrl);
    var previewTheme = url.searchParams.get('preview_theme_id');
    url.search = '';
    url.hash = '';
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    url.searchParams.set(source.type === 'dropbox' ? 'preview' : 'product_id', source.type === 'dropbox' ? encodePreview(source) : source.value);
    if (typeof text === 'string') url.searchParams.set('message', giftMessage(text));
    ['eyebrow', 'heading', 'signoff'].forEach(function (key) {
      if (copy && typeof copy[key] === 'string') url.searchParams.set(key, giftField(key, copy[key]));
    });
    return url.href;
  }

  function manifestUrl(pattern, id) {
    if (pattern.indexOf('{product_id}') === -1) throw new Error('The product preview service needs to be configured.');
    var url = new URL(pattern.replace(/\{product_id\}/g, productId(id)));
    if (url.protocol !== 'https:') throw new Error('The product preview service needs an HTTPS URL.');
    return url.href;
  }

  function tileSource(manifest, url) {
    if (!manifest || typeof manifest !== 'object') throw new Error('This product has no usable zoom artwork.');
    var source = manifest.dziXml || manifest.iiifInfo || manifest.infoJson || manifest.url ||
      (manifest.tileSources && manifest.tileSources[0]) || (manifest.sources && manifest.sources[0]);
    if (typeof source !== 'string') throw new Error('This product has no usable zoom artwork.');
    var resolved = new URL(source, url);
    if (resolved.protocol !== 'https:') throw new Error('The artwork tiles need an HTTPS URL.');
    return resolved.href;
  }

  // Keep URL handling testable without creating a browser viewer.
  if (typeof module === 'object' && module.exports) {
    module.exports = { productId: productId, directDropboxUrl: directDropboxUrl, sourceFromQuery: sourceFromQuery, sourceFromInput: sourceFromInput, createPreviewLink: createPreviewLink, manifestUrl: manifestUrl, tileSource: tileSource };
    return;
  }

  var osdPromise;
  function loadOSD(url) {
    if (window.OpenSeadragon) return Promise.resolve();
    if (!osdPromise) {
      osdPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = url;
        script.onload = function () {
          if (window.OpenSeadragon) resolve();
          else { osdPromise = null; reject(new Error('The zoom viewer could not load. Please try again.')); }
        };
        script.onerror = function () { osdPromise = null; script.remove(); reject(new Error('The zoom viewer could not load. Please try again.')); };
        document.head.appendChild(script);
      });
    }
    return osdPromise;
  }

  function init(root) {
    if (root.giftPreview) return;
    var scene = root.querySelector('[data-preview-scene]');
    var frame = root.querySelector('[data-preview-frame]');
    var artwork = root.querySelector('[data-preview-artwork]');
    var placeholder = root.querySelector('[data-preview-placeholder]');
    var loading = root.querySelector('[data-preview-loading]');
    var status = root.querySelector('[data-preview-status]');
    var admin = root.querySelector('[data-preview-admin]');
    var form = root.querySelector('[data-preview-form]');
    var input = root.querySelector('[data-preview-input]');
    var intro = root.querySelector('[data-preview-intro]');
    var messageInput = root.querySelector('[data-preview-message]');
    var defaultIntro = intro.textContent;
    var copyNodes = {}, copyInputs = {}, copyDefaults = {};
    ['eyebrow', 'heading', 'signoff'].forEach(function (key) {
      copyNodes[key] = root.querySelector('[data-preview-' + key + ']');
      copyInputs[key] = root.querySelector('[data-preview-' + key + '-input]');
      copyDefaults[key] = copyNodes[key].textContent;
    });
    var hint = root.querySelector('[data-preview-hint]');
    var output = root.querySelector('[data-preview-output]');
    var linkInput = root.querySelector('[data-preview-link]');
    var openLink = root.querySelector('[data-preview-open]');
    var copyButton = root.querySelector('[data-preview-copy]');
    var copyStatus = root.querySelector('[data-preview-copy-status]');
    var brand = document.querySelector('[data-preview-brand]');
    var viewer, abort, timer, nudgeTimer, generation = 0, disposed = false, artworkReady = false, interacted = false;
    var motionPreference = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

    function message(text, busy) {
      status.textContent = text;
      scene.setAttribute('aria-busy', busy ? 'true' : 'false');
      loading.hidden = !busy;
    }

    function clear() {
      cancelNudge();
      artworkReady = false;
      interacted = false;
      generation++;
      clearTimeout(timer);
      if (abort) { abort.abort(); abort = null; }
      if (viewer) { viewer.destroy(); viewer = null; }
      artwork.replaceChildren();
      scene.style.minHeight = '';
      frame.hidden = true;
      loading.hidden = true;
      placeholder.hidden = false;
      hint.hidden = true;
    }

    function fit(width, height) {
      if (!(width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height))) {
        throw new Error('The artwork dimensions could not be read.');
      }
      var ratio = width / height;
      frame.style.width = '';
      frame.style.height = 'auto';
      frame.style.aspectRatio = String(ratio);
      scene.style.minHeight = '0';
      frame.hidden = false;
      placeholder.hidden = true;
    }

    function fail(error, token) {
      if (disposed || token !== generation) return;
      clear();
      placeholder.textContent = error.message;
      message(error.message, false);
    }

    function ready(text, token) {
      if (disposed || token !== generation) return;
      clearTimeout(timer);
      message(text, false);
    }

    function openViewer(tiles, isDropbox, token) {
      if (disposed || token !== generation) return;
      // Start with a visible viewport; the source's actual dimensions set the frame on open.
      fit(1, 1);
      // Match the PDP Super Zoom viewer. HTML rendering avoids CORS and giant
      // intermediate canvases when Dropbox supplies a single very large image.
      viewer = window.OpenSeadragon({
        element: artwork,
        showNavigationControl: false,
        showHomeControl: false,
        showFullPageControl: false,
        crossOriginPolicy: isDropbox ? false : 'Anonymous',
        ajaxWithCredentials: false,
        useCanvas: !isDropbox,
        visibilityRatio: 1,
        constrainDuringPan: true,
        homeFillsViewer: true,
        minZoomImageRatio: 1,
        maxZoomPixelRatio: 2.6,
        blendTime: 0.1,
        immediateRender: true,
        animationTime: 0.3,
        gestureSettingsMouse: { scrollToZoom: true, clickToZoom: true, dragToPan: true },
        gestureSettingsTouch: { pinchToZoom: true, dblClickToZoom: true, dragToPan: true }
      });
      var item, hasDrawn = false, initiallyReady = false;
      function updateZoomLimit() {
        if (disposed || token !== generation || !item) return;
        var dimensions = item.getContentSize();
        // Below 4,000px on the longest side, allow at most 3x the fitted view.
        // Larger sources retain OpenSeadragon's existing native-resolution limit.
        var smallSource = Math.max(dimensions.x, dimensions.y) < 4000;
        viewer.viewport.maxZoomLevel = smallSource ? viewer.viewport.getHomeZoom() * 3 : null;
        viewer.viewport.applyConstraints(true);
      }
      function revealArtwork() {
        if (disposed || token !== generation || initiallyReady || !hasDrawn || !item || !item.getFullyLoaded()) return;
        initiallyReady = true;
        artworkReady = true;
        hint.hidden = false;
        startNudge();
        ready('Your gift preview. Scroll or pinch to zoom; drag to explore.', token);
      }
      viewer.addHandler('open', function () {
        if (disposed || token !== generation) return;
        try {
          item = viewer.world.getItemAt(0);
          item.addHandler('fully-loaded-change', revealArtwork);
          var dimensions = item.getContentSize();
          fit(dimensions.x, dimensions.y);
          viewer.viewport.resize(new window.OpenSeadragon.Point(artwork.clientWidth, artwork.clientHeight), true);
          viewer.viewport.goHome(true);
          updateZoomLimit();
          viewer.forceRedraw();
        } catch (error) { fail(error, token); }
      });
      viewer.addHandler('after-resize', updateZoomLimit);
      viewer.addHandler('tile-drawn', function () {
        if (disposed || token !== generation) return;
        hasDrawn = true;
        revealArtwork();
      });
      viewer.addHandler('open-failed', function () {
        fail(new Error(isDropbox
          ? 'The Dropbox image could not load. Check that the link opens an image and is accessible to anyone with the link.'
          : 'The artwork tiles could not load. Please try again or use a Dropbox image link.'), token);
      });
      viewer.open(tiles);
    }

    function show(source) {
      clear();
      setBrand(false);
      var token = generation;
      if (!admin.hidden) input.value = source.value;
      placeholder.textContent = 'Loading your artwork…';
      message(source.type === 'dropbox' ? 'Loading your image. Large images may take a moment.' : 'Loading your product artwork…', true);
      timer = setTimeout(function () {
        fail(new Error('The artwork took too long to load. Please try again.'), token);
      }, 90000);

      if (source.type === 'dropbox') {
        loadOSD(root.dataset.osdUrl).then(function () {
          openViewer({ type: 'image', url: source.url, buildPyramid: false, crossOriginPolicy: false, useCanvas: false }, true, token);
        }).catch(function (error) { fail(error, token); });
        return;
      }

      abort = new AbortController();
      var url;
      try { url = manifestUrl(root.dataset.manifestPattern, source.value); }
      catch (error) { fail(error, token); return; }
      loadProductBrand(source.value, abort.signal, token);
      Promise.all([
        loadOSD(root.dataset.osdUrl),
        fetch(url, { signal: abort.signal }).then(function (response) {
          if (!response.ok) throw new Error('No zoom artwork was found for this product ID. Check the ID or use a Dropbox image link.');
          return response.json();
        })
      ]).then(function (results) {
        if (disposed || token !== generation) return;
        openViewer(tileSource(results[1], url), false, token);
      }).catch(function (error) {
        if (error.name !== 'AbortError') fail(error, token);
      });
    }

    function setBrand(isArt) {
      if (!brand) return;
      brand.querySelector('[data-preview-map-brand]').hidden = isArt;
      brand.querySelector('[data-preview-art-brand]').hidden = !isArt;
      var link = brand.querySelector('[data-preview-brand-link]');
      link.href = isArt ? brand.dataset.artHome : brand.dataset.mapHome;
      link.setAttribute('aria-label', (isArt ? 'The Unique Art Co.' : brand.dataset.mapName) + ' home');
    }

    function loadProductBrand(id, signal, token) {
      if (!brand) return;
      fetch(brand.dataset.productApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: signal,
        body: JSON.stringify({
          query: 'query GiftPreviewProduct($id: ID!) { product(id: $id) { id productType } }',
          variables: { id: 'gid://shopify/Product/' + productId(id) }
        })
      }).then(function (response) {
        if (!response.ok) throw new Error('Product type lookup failed');
        return response.json();
      }).then(function (result) {
        if (disposed || token !== generation) return;
        var product = result.data && result.data.product;
        setBrand(!!product && product.id === 'gid://shopify/Product/' + id && product.productType === 'Fine art');
      }).catch(function () {
        // Branding lookup is optional: it must not prevent the artwork loading.
        if (!disposed && token === generation) setBrand(false);
      });
    }

    function readQuery() {
      setBrand(false);
      var params = new URLSearchParams(window.location.search);
      var adminMode = params.get('admin') === 'yes';
      admin.hidden = !adminMode;
      if (!adminMode) input.value = '';
      output.hidden = true;
      copyStatus.textContent = '';
      setIntro(defaultIntro);
      ['eyebrow', 'heading', 'signoff'].forEach(function (key) { setCopy(key, copyDefaults[key]); });
      try {
        if (params.has('message')) setIntro(giftMessage(params.get('message')));
        ['eyebrow', 'heading', 'signoff'].forEach(function (key) {
          if (params.has(key)) setCopy(key, giftField(key, params.get(key)));
        });
        var source = sourceFromQuery(window.location.search);
        if (source) {
          if (adminMode) displayLink(source);
          show(source);
        }
        else {
          clear();
          input.value = '';
          var text = adminMode ? 'Create a link below to preview your gift.' : 'This preview link does not include artwork.';
          placeholder.textContent = text;
          message(text, false);
        }
      } catch (error) { fail(error, generation); }
    }

    function setIntro(text) {
      intro.textContent = text;
      intro.hidden = !text.trim();
      messageInput.value = text;
    }

    function setCopy(key, text) {
      copyNodes[key].textContent = text;
      copyNodes[key].hidden = !text.trim();
      copyInputs[key].value = text;
    }

    function displayLink(source) {
      var text = giftMessage(messageInput.value);
      var copy = {};
      ['eyebrow', 'heading', 'signoff'].forEach(function (key) {
        copy[key] = giftField(key, copyInputs[key].value);
      });
      var link = createPreviewLink(window.location.href, source, text, copy);
      linkInput.value = link;
      openLink.href = link;
      output.hidden = false;
      copyStatus.textContent = '';
      return link;
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (admin.hidden) return;
      try {
        setIntro(giftMessage(messageInput.value));
        ['eyebrow', 'heading', 'signoff'].forEach(function (key) { setCopy(key, giftField(key, copyInputs[key].value)); });
        var source = sourceFromInput(input.value);
        var url = new URL(displayLink(source));
        url.searchParams.set('admin', 'yes');
        window.history.pushState(null, '', url.href);
        show(source);
      } catch (error) { output.hidden = true; fail(error, generation); }
    });

    function invalidateLink() {
      output.hidden = true;
      copyStatus.textContent = '';
    }
    input.addEventListener('input', invalidateLink);
    ['eyebrow', 'heading', 'signoff'].forEach(function (key) {
      copyInputs[key].addEventListener('input', function () {
        setCopy(key, copyInputs[key].value);
        invalidateLink();
      });
    });
    messageInput.addEventListener('input', function () {
      intro.textContent = messageInput.value;
      intro.hidden = !messageInput.value.trim();
      invalidateLink();
    });

    function cancelNudge() {
      clearInterval(nudgeTimer);
      nudgeTimer = null;
      frame.classList.remove('gift-preview__frame--nudge');
    }

    function startNudge() {
      cancelNudge();
      if (!artworkReady || interacted || disposed || (motionPreference && motionPreference.matches)) return;
      nudgeTimer = setInterval(function () {
        if (disposed || interacted || (motionPreference && motionPreference.matches)) { cancelNudge(); return; }
        if (document.visibilityState === 'hidden') return;
        var bounds = frame.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        frame.classList.remove('gift-preview__frame--nudge');
        void frame.offsetWidth;
        frame.classList.add('gift-preview__frame--nudge');
      }, 3000);
    }

    function stopNudge() {
      if (!artworkReady) return;
      interacted = true;
      cancelNudge();
    }
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (type) {
      artwork.addEventListener(type, stopNudge, { capture: true, passive: true });
    });
    frame.addEventListener('animationend', function (event) {
      if (event.animationName === 'gift-preview-nudge') frame.classList.remove('gift-preview__frame--nudge');
    });
    if (motionPreference && motionPreference.addEventListener) motionPreference.addEventListener('change', startNudge);
    artwork.addEventListener('keydown', function (event) {
      if (event.target !== artwork || !viewer || !artworkReady) return;
      var vp = viewer.viewport;
      if (event.key === '+' || event.key === '=') vp.zoomBy(1.5);
      else if (event.key === '-') vp.zoomBy(1 / 1.5);
      else if (event.key === 'Home' || event.key === '0') vp.goHome();
      else if (event.key === 'ArrowLeft') vp.panBy(new window.OpenSeadragon.Point(-0.05, 0));
      else if (event.key === 'ArrowRight') vp.panBy(new window.OpenSeadragon.Point(0.05, 0));
      else if (event.key === 'ArrowUp') vp.panBy(new window.OpenSeadragon.Point(0, -0.05));
      else if (event.key === 'ArrowDown') vp.panBy(new window.OpenSeadragon.Point(0, 0.05));
      else return;
      event.preventDefault();
      vp.applyConstraints();
    });

    copyButton.addEventListener('click', function () {
      var value = linkInput.value;
      function selectLink() {
        if (disposed) return;
        linkInput.focus();
        linkInput.select();
        copyStatus.textContent = 'Link selected. Copy it using your keyboard or device menu.';
      }
      if (!navigator.clipboard || !navigator.clipboard.writeText) { selectLink(); return; }
      navigator.clipboard.writeText(value).then(function () {
        if (!disposed) copyStatus.textContent = 'Link copied.';
      }).catch(selectLink);
    });

    window.addEventListener('popstate', readQuery);
    root.giftPreview = { destroy: function () {
      disposed = true;
      clear();
      window.removeEventListener('popstate', readQuery);
      if (motionPreference && motionPreference.removeEventListener) motionPreference.removeEventListener('change', startNudge);
    } };
    readQuery();
  }

  function initAll(scope) { scope.querySelectorAll('[data-gift-preview]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-gift-preview]').forEach(function (root) { if (root.giftPreview) root.giftPreview.destroy(); });
  });
})();
